/**
 * Google Analytics / Google Ads - Consent Mode v2
 * 
 * Implementa Google Consent Mode para cumplir con GDPR.
 * El script se carga siempre pero las cookies solo se activan tras consentimiento.
 * 
 * @see https://developers.google.com/tag-platform/security/guides/consent
 */

import { maskedAnalyticsPage, type MaskedAnalyticsPage } from '@/lib/analytics-url-policy'
import { isNativeAppRuntime } from '@/lib/native-app-runtime'
import { scheduleWhenIdle } from '@/lib/schedule-idle'

// ID de Google Ads (se puede configurar via env para diferentes entornos)
export const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID || 'AW-17789051629'

// Estado de inicialización
let isInitialized = false

// Debug mode
const DEBUG_GA = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_DEBUG_AUTH === 'true'

function logDebug(message: string, data?: unknown) {
  if (DEBUG_GA && typeof window !== 'undefined') {
    console.log(`[GoogleAds] ${message}`, data ?? '')
  }
}

/**
 * Declaración de tipos para gtag
 */
declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

/**
 * Función wrapper para gtag
 */
export function gtag(...args: unknown[]): void {
  if (typeof window === 'undefined') return
  
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(args)
}

/**
 * Inicializa Google Analytics con Consent Mode v2
 * Se llama una sola vez al cargar la página
 * Por defecto, todo está denegado hasta que el usuario dé consentimiento
 */
export function initGoogleAnalytics(): void {
  if (typeof window === 'undefined') return
  if (isNativeAppRuntime()) return
  if (isInitialized) return

  // Inicializar dataLayer
  window.dataLayer = window.dataLayer || []
  
  // Definir función gtag
  window.gtag = function gtag() {
     
    window.dataLayer.push(arguments)
  }

  // Configurar consent mode ANTES de cargar gtag.js
  // Por defecto todo denegado (GDPR compliant)
  window.gtag('consent', 'default', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied',
    'functionality_storage': 'denied',
    'personalization_storage': 'denied',
    'security_storage': 'granted', // Siempre permitido para seguridad
    'wait_for_update': 500, // Esperar 500ms para actualización de consent
  })

  // Marcar como inicializado antes de cargar el script
  isInitialized = true

  // Dejar js/config en dataLayer antes de que cualquier conversión se encole.
  configureGtag()

  // Cargar script de gtag.js
  loadGtagScript()

  logDebug('✅ Google Analytics initialized with consent mode (default: denied)')
}

/**
 * Carga el script de gtag.js dinámicamente.
 *
 * El fetch+eval de gtag.js costaba ~133 ms de frame bloqueante justo tras `load`
 * (perfilado LoAF en producción, 2026-07), lo que se heredaba como input delay
 * en el primer toque del visitante. Se aplaza a idle; el Consent Mode ya quedó
 * configurado de forma síncrona en `initGoogleAnalytics`.
 */
function loadGtagScript(): void {
  if (typeof window === 'undefined') return
  scheduleWhenIdle(injectGtagScript)
}

function injectGtagScript(): void {
  // Verificar si ya existe el script
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) return

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`
  // En una página enmascarada, ni el `Referer` de la propia petición del script.
  if (maskedAnalyticsPage()) script.referrerPolicy = 'no-referrer'
  script.onload = () => logDebug('✅ gtag.js script loaded')
  script.onerror = () => {
    console.error('[GoogleAds] Failed to load gtag.js script')
  }
  
  document.head.appendChild(script)
}

/** Encola la configuración antes de cargar el script para preservar el orden de los eventos. */
function configureGtag(): void {
  if (typeof window === 'undefined' || !window.gtag) return

  window.gtag('js', new Date())
  window.gtag('config', GA_TRACKING_ID, {
    // Deshabilitar el envío automático de page_view hasta consentimiento
    'send_page_view': false,
    ...maskedPageParams(maskedAnalyticsPage()),
  })

  logDebug('✅ gtag configured', { id: GA_TRACKING_ID })
}

/** Lo que sustituye a la URL real en un hit de una página enmascarada. */
function maskedPageParams(page: MaskedAnalyticsPage | null): Record<string, string> {
  if (!page) return {}
  return { page_location: page.location, page_referrer: '', page_title: page.title }
}

/**
 * Actualiza el estado de consentimiento cuando el usuario acepta
 */
export function grantConsent(): void {
  if (isNativeAppRuntime()) return

  if (typeof window === 'undefined' || !window.gtag) {
    logDebug('⚠️ Cannot grant consent - gtag not available')
    return
  }

  window.gtag('consent', 'update', {
    'ad_storage': 'granted',
    'ad_user_data': 'granted',
    'ad_personalization': 'granted',
    'analytics_storage': 'granted',
    'functionality_storage': 'granted',
    'personalization_storage': 'granted',
  })

  // Enviar page view ahora que tenemos consentimiento. Una página enmascarada
  // no es una visita a la página neutra: no se cuenta como tal.
  if (!maskedAnalyticsPage()) {
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
    })
  }

  logDebug('✅ Consent granted - all tracking enabled')
}

/**
 * Actualiza el estado de consentimiento cuando el usuario rechaza
 */
export function denyConsent(): void {
  if (isNativeAppRuntime()) return

  if (typeof window === 'undefined' || !window.gtag) {
    logDebug('⚠️ Cannot deny consent - gtag not available')
    return
  }

  window.gtag('consent', 'update', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied',
    'functionality_storage': 'denied',
    'personalization_storage': 'denied',
  })

  logDebug('✅ Consent denied - tracking disabled')
}

/**
 * Verifica si Google Analytics está inicializado
 */
export function isGoogleAnalyticsInitialized(): boolean {
  if (isNativeAppRuntime()) return false
  return isInitialized && typeof window !== 'undefined' && !!window.gtag
}

/**
 * Envía un evento de conversión a Google Ads
 * Útil para tracking de compras, registros, etc.
 * 
 * @example
 * trackConversion('AW-17789051629/AbCdEfGhIjKl', { value: 9.99, currency: 'EUR' })
 */
export function trackConversion(
  conversionId: string, 
  params?: { value?: number; currency?: string; transaction_id?: string }
): void {
  if (isNativeAppRuntime()) {
    logDebug('Skipping Google Ads conversion in native app runtime', { conversionId })
    return
  }

  if (typeof window === 'undefined' || !window.gtag) {
    logDebug('⚠️ Cannot track conversion - gtag not available')
    return
  }

  // Se lee la ubicación al ENVIAR, no al configurar: el enmascarado no puede
  // depender de en qué página se inicializó gtag.
  window.gtag('event', 'conversion', {
    'send_to': conversionId,
    ...params,
    ...maskedPageParams(maskedAnalyticsPage()),
  })

  logDebug('📊 Conversion tracked', { conversionId, params })
}

/**
 * Envía un evento personalizado a Google Analytics
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (isNativeAppRuntime()) {
    logDebug('Skipping Google Analytics event in native app runtime', { eventName })
    return
  }

  if (typeof window === 'undefined' || !window.gtag) {
    logDebug('⚠️ Cannot track event - gtag not available')
    return
  }

  window.gtag('event', eventName, { ...params, ...maskedPageParams(maskedAnalyticsPage()) })
  logDebug('📊 Event tracked', { eventName, params })
}
